import { Button } from "@svea/design-system-components-react";
import { Suspense, useId } from "react";
import { Modal, ModalOverlay, Dialog as RADialog } from "react-aria-components";
import { tw } from "../../styling";
import { Spinner } from "../spinner";
import { SvgIcon } from "../svg-icon";
import { Heading } from "../typography";
import {
	containerClasses,
	containerPaddings,
	contentClasses,
	footerClasses,
	headerClasses,
	type IconVariant,
	iconBackgroundColor,
	iconClasses,
	iconColor,
	iconSrc,
	modalSize,
	wrapperClasses,
} from "./styles";

type FooterProps = { children: React.ReactNode };
const DialogFooter = ({ children }: FooterProps) => <div className={footerClasses}>{children}</div>;

type ContentProps = {
	children: React.ReactNode;
};
const DialogContent = ({ children }: ContentProps) => (
	<div className="flex w-full flex-col">{children}</div>
);

type IconProps = {
	variant?: IconVariant;
	className?: string;
};
const DialogIcon = ({ variant = "success", className }: IconProps) => (
	<div className={tw(iconClasses, iconBackgroundColor[variant], iconColor[variant])}>
		<SvgIcon imageSrc={iconSrc[variant]} height={40} className={className} />
	</div>
);

type Props = React.AriaAttributes &
	({
		isOpen: boolean;
		children: React.ReactNode;
		center?: boolean;
		size?: keyof typeof modalSize;
		withPadding?: boolean;
		withOverflow?: boolean;
	} & (
		| {
				heading: string;
				icon?: IconVariant;
				"aria-label"?: never;
		  }
		| { "aria-label": string; heading?: never; icon?: never }
	) &
		(
			| {
					isDismissable?: true;
					onClose?: () => void;
					setOpen: (open: boolean) => void;
			  }
			| {
					isDismissable: false;
					onClose?: never;
					setOpen?: never;
			  }
		));
export const Dialog = ({
	children,
	isOpen,
	isDismissable = true,
	setOpen,
	size = "auto",
	withPadding = true,
	center = true,
	withOverflow = false,
	heading,
	icon,
	onClose,
}: Props) => {
	const headerId = useId();
	return (
		<ModalOverlay
			isOpen={isOpen}
			isDismissable={isDismissable}
			onOpenChange={(isOpen) => {
				if (!isOpen) onClose?.();
				setOpen?.(isOpen);
			}}
			className="tablet:px-12 fixed top-0 left-0 z-999 flex h-dvh w-dvw items-center justify-center bg-black/75 px-4 print:absolute"
		>
			<Modal className={tw(wrapperClasses, modalSize[size])} aria-modal="true">
				<RADialog
					data-testid="modalContainer"
					aria-labelledby={headerId}
					className={tw(containerClasses, withPadding && containerPaddings)}
				>
					{!!isDismissable && (
						<div className="absolute top-6 right-6 z-200 print:hidden">
							<Button
								variant="icon"
								icon="x"
								onPress={() => {
									onClose?.();
									setOpen?.(false);
								}}
								aria-label="Stäng"
							/>
						</div>
					)}
					<article
						className={tw(
							contentClasses,
							center && "items-center text-center",
							size === "full" && "tablet:gap-4",
							withOverflow && "overflow-visible",
						)}
					>
						<Suspense
							fallback={
								<div className="flex items-center justify-center">
									<Spinner size={64} />
								</div>
							}
						>
							{heading && (
								<div className={tw("print:hidden", headerClasses, icon && "items-center")}>
									{icon && <DialogIcon variant={icon} />}
									<Heading.H1 size="h3" italic={false} id={headerId}>
										{heading}
									</Heading.H1>
								</div>
							)}
							{children}
						</Suspense>
					</article>
				</RADialog>
			</Modal>
		</ModalOverlay>
	);
};

Dialog.Content = DialogContent;
Dialog.Footer = DialogFooter;
